import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Rating,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Alert,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { FeedbackProps, CreateFeedbackDto, UpdateFeedbackDto } from "./types";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_FEEDBACK, GET_FEEDBACK, UPDATE_FEEDBACK } from "./queries";
import EditPencilIcon from "assets/svg/EditPencilIcon";
import * as styles from "./styles";

const feedbackReasons = [
  {
    value: "MISSING_PORTIONS",
    label: "Transcript is missing portions of the audio",
  },
  {
    value: "SPEAKER_COLOR_CODING_MISMATCH",
    label: "Speaker identification doesn't always match up with the audio",
  },
  {
    value: "TRANSCRIPTION_NOT_MATCHING",
    label: "Words transcribed don't match what was said",
  },
  {
    value: "MISMATCH_OF_ORDER",
    label: "Order of words/sentences doesn't match timing in audio",
  },
  {
    value: "EXTRA_WORDS_TRANSCRIPT",
    label: "Transcript has extra words/sentences not spoken in audio",
  },
];

export const CallFeedback = ({ callSid }: FeedbackProps) => {
  const [rating, setRating] = useState<number | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [showValidationError, setShowValidationError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);

  const [originalRating, setOriginalRating] = useState<number | null>(null);
  const [originalReasons, setOriginalReasons] = useState<string[]>([]);
  const [originalDescription, setOriginalDescription] = useState("");

  const {
    data: existingFeedback,
    loading: feedbackLoading,
    refetch,
  } = useQuery(GET_FEEDBACK, {
    variables: { id: callSid },
    skip: !callSid,
    fetchPolicy: "network-only",
  });

  const [createFeedback, { loading: submitLoading }] = useMutation(
    CREATE_FEEDBACK,
    {
      onCompleted: () => {
        setShowTooltip(true);
        setIsEditing(false);
        refetch();

        setTimeout(() => {
          setShowTooltip(false);
        }, 2000);
      },
      onError: (error) => {
        console.error("Error submitting feedback:", error);
      },
    }
  );

  const [updateFeedback, { loading: updateLoading }] = useMutation(
    UPDATE_FEEDBACK,
    {
      onCompleted: () => {
        setShowTooltip(true);
        setIsEditing(false);
        refetch();

        setTimeout(() => {
          setShowTooltip(false);
        }, 2000);
      },
      onError: (error) => {
        console.error("Error updating feedback:", error);
      },
    }
  );

  useEffect(() => {
    if (existingFeedback?.getFeedback) {
      const feedback = existingFeedback.getFeedback;
      const feedbackRating = feedback.rating;
      let feedbackReasons: string[] = [];

      if (feedback.reason) {
        if (Array.isArray(feedback.reason)) {
          feedbackReasons = feedback.reason;
        } else if (typeof feedback.reason === "string") {
          feedbackReasons = feedback.reason.includes(",")
            ? feedback.reason.split(",")
            : [feedback.reason];
        }
      }

      const feedbackDescription = feedback.description || "";

      setRating(feedbackRating);
      setSelectedReasons(feedbackReasons);
      setDescription(feedbackDescription);

      setOriginalRating(feedbackRating);
      setOriginalReasons(feedbackReasons);
      setOriginalDescription(feedbackDescription);

      setHasExistingFeedback(true);
      setIsEditing(false);
    } else {
      setHasExistingFeedback(false);
      setIsEditing(true);
    }
  }, [existingFeedback]);

  const handleRatingChange = (
    _event: React.SyntheticEvent,
    newValue: number | null
  ) => {
    // Prevent clearing the rating by clicking the same star
    if (newValue === null && rating !== null) {
      return;
    }
    
    setRating(newValue);
    if (newValue === 5) {
      setSelectedReasons([]);
      setShowValidationError(false);
    }
  };

  const handleReasonToggle = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
    setShowValidationError(false);
  };

  const handleSubmit = async () => {
    if (!callSid || !rating) return;

    if (rating < 5 && selectedReasons.length === 0 && !description.trim()) {
      setShowValidationError(true);
      return;
    }

    try {
      if (hasExistingFeedback) {
        const updateData: UpdateFeedbackDto = {
          callSid,
          rating,
          reason: rating === 5 ? [] : selectedReasons,
          description: description.trim() || null,
        };

        await updateFeedback({
          variables: {
            data: updateData,
          },
        });
      } else {
        const createData: CreateFeedbackDto = {
          callSid,
          rating,
          reason: rating === 5 ? [] : selectedReasons,
          description: description.trim() || "",
        };

        await createFeedback({
          variables: {
            data: createData,
          },
        });
      }
    } catch (error) {
      console.error("Error handling feedback:", error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setRating(null);
    setSelectedReasons([]);
    setDescription("");
    setShowValidationError(false);

    if (hasExistingFeedback) {
      setIsEditing(false);
    }
  };

  const isLoading = submitLoading || updateLoading || feedbackLoading;

  const hasFormChanged = () => {
    if (!hasExistingFeedback) return true;

    if (rating !== originalRating) return true;

    if (selectedReasons.length !== originalReasons.length) return true;
    if (selectedReasons.some((reason) => !originalReasons.includes(reason)))
      return true;

    if (description !== originalDescription) return true;

    return false;
  };

  const isSubmitDisabled =
    !rating ||
    !callSid ||
    isLoading ||
    (rating < 5 && selectedReasons.length === 0 && !description.trim()) ||
    (!isEditing && hasExistingFeedback) ||
    (hasExistingFeedback && isEditing && !hasFormChanged());

  return (
    <Paper
      elevation={3}
      sx={styles.paperStyles}
    >
      <Box sx={styles.headerBoxStyles}>
        <Box sx={styles.titleBoxStyles}>
          <Typography variant="h6" sx={styles.titleTypographyStyles}>
            Transcript Feedback
          </Typography>
        </Box>
        {hasExistingFeedback && !isEditing && (
          <Box sx={styles.feedbackSubmittedContainerStyles}>
            <Alert severity="success" sx={styles.feedbackSubmittedAlertStyles}>
              Feedback Submitted
            </Alert>

            <IconButton
              onClick={handleEdit}
              size="small"
              sx={styles.editButtonStyles}
              title="Edit feedback"
            >
              <EditPencilIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Success Tooltip */}
      {showTooltip && (
        <Box sx={styles.successTooltipStyles}>
          <Box sx={styles.successIconStyles}>
            ✓
          </Box>
          Thank you! Your feedback has been submitted.
        </Box>
      )}

      <Box sx={styles.ratingBoxStyles}>
        <Typography
          component="legend"
          sx={styles.ratingLabelStyles}
        >
          How usable was this transcript?
        </Typography>
        <Box sx={styles.ratingContainerStyles}>
          <Rating
            name="transcript-rating"
            value={rating || 0}
            onChange={handleRatingChange}
            size="large"
            disabled={!isEditing}
            icon={<Star fontSize="inherit" />}
            emptyIcon={<StarBorder fontSize="inherit" />}
            sx={styles.getRatingStyles()}
          />
          {rating && (
            <Typography variant="h6" color="text.secondary">
              {rating} {rating === 1 ? "star" : "stars"}
            </Typography>
          )}
        </Box>
      </Box>

      {rating && rating < 5 && (
        <>
          <Divider sx={styles.dividerStyles} />

          <Box sx={styles.reasonsBoxStyles}>
            <Typography component="legend" gutterBottom>
              What issues did you experience? (Select all that apply)
            </Typography>
            <FormControl component="fieldset" error={showValidationError}>
              <FormGroup>
                {feedbackReasons.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    control={
                      <Checkbox
                        checked={selectedReasons.includes(reason.value)}
                        onChange={() => handleReasonToggle(reason.value)}
                        disabled={!isEditing}
                        sx={styles.getCheckboxStyles(isEditing)}
                      />
                    }
                    label={reason.label}
                    sx={styles.checkboxLabelStyles}
                  />
                ))}
              </FormGroup>
              {showValidationError && (
                <Typography color="error" variant="h4" sx={styles.validationErrorStyles}>
                  Please select a reason or provide a description below
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box sx={styles.additionalFeedbackBoxStyles}>
            <Typography
              sx={styles.additionalFeedbackLabelStyles}
            >
              Additional Feedback (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Describe any other issues or provide additional context..."
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setDescription(e.target.value);
                  setShowValidationError(false);
                }
              }}
              disabled={!isEditing}
              helperText={`${description.length}/500 characters`}
              variant="outlined"
              sx={styles.getTextFieldStyles(isEditing)}
            />
          </Box>
        </>
      )}

      {(isEditing || !hasExistingFeedback) && (
        <Box sx={styles.actionButtonsBoxStyles}>
          <Button
            className="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            startIcon={
              isLoading ? <CircularProgress color="inherit" size={20} /> : null
            }
          >
            {isLoading
              ? hasExistingFeedback
                ? "Updating..."
                : "Submitting..."
              : hasExistingFeedback
              ? "Update Feedback"
              : "Submit Feedback"}
          </Button>
          {hasExistingFeedback && isEditing && (
            <Button
              variant="outlined"
              className="button"
              onClick={() => {
                setRating(originalRating);
                setSelectedReasons(originalReasons);
                setDescription(originalDescription);
                setIsEditing(false);
                setShowValidationError(false);
              }}
            >
              Cancel
            </Button>
          )}
          {!hasExistingFeedback &&
            rating &&
            (rating < 5 || description.trim()) && (
              <Button
                className="button"
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
        </Box>
      )}
    </Paper>
  );
};
